import { useState, useEffect } from "react";

const CHECKLIST = [
  {
    section: "Plant CRUD",
    items: [
      { id: "create-simple", label: "Create a simple plant (genus + species only)", steps: "Go to /new → Enter Manually → fill genus & species → Save. Verify plant detail shows correct name and auto-generated specimen ID." },
      { id: "edit-name", label: "Edit plant name — specimen ID regenerates", steps: "Open edit on any plant → change species → Save. Verify specimen ID prefix changed to match new species." },
      { id: "edit-nonname", label: "Edit non-name field — specimen ID stays stable", steps: "Open edit → change status or notes only → Save. Verify specimen ID is unchanged and no phantom history." },
      { id: "schedule-from-detail", label: "Schedule care from plant detail", steps: "Open any plant detail → Schedule → choose task/date/recurrence → Add Task. Verify the toast appears, then open Plan and confirm the task is present for that plant." },
      { id: "delete-plant", label: "Delete a plant (cascade)", steps: "Open edit → Delete Plant → confirm. Verify redirect to list and plant is gone." },
      { id: "manual-id-create", label: "Manual specimen ID on create", steps: "Create plant with a custom specimen ID typed in. Verify it uses your ID, not auto-generated." },
      { id: "manual-id-edit", label: "Manual specimen ID on edit", steps: "Edit a plant → change specimen ID → confirm danger dialog. Verify ID updated in detail and history." },
    ]
  },
  {
    section: "Botanical Naming",
    items: [
      { id: "cultivar", label: "Cultivar — create and round-trip", steps: "Create with cultivar (e.g. 'Thai Constellation'). Verify casing preserved. Edit and re-save — no duplication." },
      { id: "hybrid-inter", label: "Interspecific hybrid — create and round-trip", steps: "Create with Interspecific Hybrid checked, fill mother + father species. Verify × separator. Edit and re-save — no doubled ×." },
      { id: "hybrid-genus", label: "Intergeneric hybrid — create and round-trip", steps: "Create with Intergeneric Hybrid checked, fill both genera. Verify 'Genus × Genus' format, both capitalized, ID uses ALO×COL format. Re-save clean." },
      { id: "rank", label: "Infraspecific rank — create and round-trip", steps: "Create with var./subsp./f. rank. Verify format like 'deliciosa var. sierrana'. Edit and re-save — no duplication." },
      { id: "combo-hybrid-cult", label: "Combined: hybrid + cultivar", steps: "Create with hybrid + cultivar. Verify species like 'melo × reginula \\'Black\\''. Round-trip clean." },
      { id: "combo-rank-cult", label: "Combined: rank + cultivar", steps: "Create with rank + cultivar. Verify species like 'deliciosa var. sierrana \\'Mint\\''. Round-trip clean." },
      { id: "naming-autoexpand", label: "Naming options auto-expand on edit", steps: "Open edit on a hybrid/cultivar/rank plant. Verify naming options section is visible without clicking toggle." },
      { id: "normalization", label: "Name normalization", steps: "Create with lowercase genus (e.g. 'monstera'). Verify it auto-capitalizes to 'Monstera'. Species stays lowercase." },
    ]
  },
  {
    section: "Duplicate Detection",
    items: [
      { id: "dupe-form", label: "Duplicate check on manual form", steps: "Create a plant with same genus+species as existing. Verify duplicate panel appears with existing plant preview." },
      { id: "dupe-photo", label: "Duplicate check on photo ID flow", steps: "Photo flow → select suggestion matching existing plant. Verify duplicate panel with 'Same plant — add photo' option." },
      { id: "dupe-override", label: "Create despite duplicate", steps: "From duplicate panel, click 'Different specimen — create anyway'. Verify new plant created." },
    ]
  },
  {
    section: "Photo Flow",
    items: [
      { id: "photo-first", label: "Photo-first add flow", steps: "Go to /new → take/select photo → Identify → select suggestion → verify prefill in form → Save with photo." },
      { id: "photo-upload", label: "Photo upload from detail page", steps: "On any plant detail, upload via camera or gallery. Verify photo appears." },
      { id: "photo-lightbox", label: "Lightbox opens with PlantNet buttons", steps: "Tap a photo → lightbox opens. Verify ID Species and Check Health buttons visible." },
      { id: "photo-delete", label: "Delete a photo", steps: "In lightbox or photo list, delete a photo. Verify it's removed." },
    ]
  },
  {
    section: "Observations & Measurements",
    items: [
      { id: "obs-create", label: "Create observation", steps: "On any plant → add observation (any event type). Verify it appears in list." },
      { id: "obs-edit", label: "Edit observation", steps: "Edit an existing observation → change date or notes → Save." },
      { id: "obs-delete", label: "Delete observation", steps: "Delete an observation. Verify removal." },
      { id: "meas-create", label: "Create measurement", steps: "Add a measurement (height, leaf count, etc.). Verify it appears." },
      { id: "meas-delete", label: "Delete measurement", steps: "Delete a measurement. Verify removal." },
      { id: "activity-collapse", label: "Collapsible activity histories", steps: "Open a plant with more than three observations or measurements. Verify each header shows a count, the collapsed state still shows the latest three records, and expanding reveals the full history." },
    ]
  },
  {
    section: "Care Rounds",
    items: [
      { id: "rounds-load", label: "Care Round queue loads", steps: "Go to /rounds → choose Most Overdue. Verify the first card renders with the normal cover-cropped image, plant name, days-since badge, and action buttons." },
      { id: "rounds-image-sheet", label: "Round image opens full-fit sheet", steps: "Tap the card image. Verify the slide-up sheet opens and the image fits within the sheet without cover cropping. Close the sheet and confirm the round remains in place." },
      { id: "rounds-schedule-care", label: "Schedule Care from round", steps: "Tap Schedule Care → choose a task/date → Add Task. Verify the inline confirmation appears, then open Plan and confirm the task is scheduled for that plant/date." },
      { id: "rounds-location-update", label: "Update location from round", steps: "Tap Location → change selected location(s) → Save Location. Verify the confirmation appears and the card location chip updates." },
      { id: "rounds-check-in-suppression", label: "Looks Good advances and suppresses", steps: "Tap Looks Good → Save. Verify a health_check observation is created and the card advances out of the current round." },
    ]
  },
  {
    section: "Lookup Tables & Form Features",
    items: [
      { id: "inline-add-loc", label: "Inline '+ Add' for locations", steps: "In plant edit form, type a new location name and hit + or Enter. Verify it creates and auto-selects." },
      { id: "inline-add-tag", label: "Inline '+ Add' for tags", steps: "Same test for tags." },
      { id: "inline-add-ls", label: "Inline '+ Add' for light sources", steps: "Same test for light sources." },
      { id: "inline-add-gm", label: "Inline '+ Add' for growing methods", steps: "Same test for growing methods." },
      { id: "inline-add-sub", label: "Inline '+ Add' for substrates", steps: "Select a growing method first, then add a new substrate. Verify it links to the method." },
      { id: "cascade-dropdown", label: "Growing method → substrate cascade", steps: "Select a growing method → verify substrate dropdown filters. Change method → verify substrate resets." },
    ]
  },
  {
    section: "Plant List & Filters",
    items: [
      { id: "list-load", label: "Plant list loads", steps: "Go to /. Verify all plants appear with photos, badges, and detail tags." },
      { id: "wishlist-excluded", label: "Wishlist plants excluded from collection", steps: "If any plants have status 'wishlist', confirm they do NOT appear in the collection list. Also confirm 'wishlist' does not appear as a filter option in the status filter chips." },
      { id: "search", label: "Search works", steps: "Type a genus or common name in search. Verify filtering." },
      { id: "filter-status", label: "Filter by status", steps: "Open filters → select a status. Verify list filters." },
      { id: "filter-method", label: "Filter by growing method", steps: "Open filters → select a method. Verify list filters." },
      { id: "filter-location", label: "Filter by location", steps: "Open filters → select a location. Verify list filters." },
      { id: "filter-clear", label: "Clear all filters", steps: "With filters active, tap 'Clear all filters'. Verify full list returns." },
      { id: "sort", label: "Sort options", steps: "Change sort field and direction. Verify order changes." },
    ]
  },
  {
    section: "Bottom Navigation",
    items: [
      { id: "nav-renders", label: "Nav bar renders on top-level screens", steps: "Visit /, /stats, /settings. Verify 4-slot nav bar is visible at the bottom on all three." },
      { id: "nav-hidden-detail", label: "Nav hidden on plant detail", steps: "Open any plant detail (/plants/:id). Verify bottom nav is NOT visible." },
      { id: "nav-hidden-form", label: "Nav hidden on edit form", steps: "Go to /plants/:id/edit or /new/manual. Verify bottom nav is NOT visible." },
      { id: "nav-active-state", label: "Active tab highlights correctly", steps: "Navigate between Collection, Stats, and Settings via the nav bar. Verify the tapped tab highlights in green and others are muted." },
      { id: "nav-routing", label: "All nav slots route correctly", steps: "Tap each slot — Collection goes to /, Add goes to /new, Stats to /stats, Settings to /settings. Verify correct screen loads each time." },
      { id: "nav-slot-config", label: "Nav slot config loads from DB", steps: "Change slots in Settings, then hard-reload the page. Verify the nav still shows the updated slots (not the default Add + Stats)." },
    ]
  },
  {
    section: "Stats Screen",
    items: [
      { id: "stats-loads", label: "Stats screen loads without errors", steps: "Navigate to /stats. Verify all sections render. Check browser console for errors." },
      { id: "stats-collection", label: "Collection overview cards correct", steps: "Verify total plant count, genera count, and propagation count match your known data." },
      { id: "stats-health", label: "Collection health callouts present", steps: "Verify 'Last tended' and 'Longest held' callout rows appear with a plant name, specimen ID, and relative/absolute date." },
      { id: "stats-callout-nav", label: "Callout rows navigate to plant detail", steps: "Tap a callout row (Last tended, Longest held, or Most Active). Verify it navigates to the correct plant detail page." },
      { id: "stats-bars", label: "Mini bar charts render", steps: "Verify By Genus, By Status (if >1 status), By Location sections each show bars with counts." },
      { id: "stats-propagation", label: "Propagation section correct", steps: "Verify attempts, successful, and success rate match your known propagation records." },
      { id: "stats-financial", label: "Financial section conditional", steps: "If plants have cost recorded, verify Financial section appears. If no cost data, verify section is hidden." },
      { id: "stats-coverage", label: "Data coverage bars render", steps: "Verify all 5 coverage bars (photos, acquisition date, cost, lineage, notes) show correct counts." },
    ]
  },
  {
    section: "Settings Screen",
    items: [
      { id: "settings-loads", label: "Settings screen loads from DB", steps: "Navigate to /settings. Verify theme picker reflects current DB value (not just localStorage). Verify PlantNet toggles reflect DB state." },
      { id: "settings-theme", label: "Theme change applies immediately", steps: "In Settings, switch from System → Dark → Light. Verify the UI updates in real time without page reload. Verify each 200 response in network tab." },
      { id: "settings-theme-persist", label: "Theme persists across reload", steps: "Set theme to Dark. Hard-reload the page. Verify the app opens in dark mode." },
      { id: "settings-nav-picker", label: "Nav slot picker updates preview", steps: "In Settings, tap a nav option (e.g. Wishlist). Verify the 4-slot preview updates to show the new selection." },
      { id: "settings-nav-live", label: "Nav slot change updates nav bar live", steps: "Change a nav slot in Settings. Without reloading, verify the bottom nav bar reflects the new slot immediately." },
      { id: "settings-nav-persist", label: "Nav slot change persists across reload", steps: "Change slots, reload. Verify nav still shows updated slots." },
      { id: "settings-toggles", label: "PlantNet toggles save", steps: "Toggle PlantNet Species ID off → verify 200 in network tab. Toggle back on → verify 200. Reload and confirm state is preserved." },
    ]
  },
  {
    section: "Deploy & Infrastructure",
    items: [
      { id: "pwa-launch", label: "PWA launches from home screen", steps: "Open from iOS home screen. Verify standalone mode (no Safari chrome)." },
      { id: "api-docs", label: "API docs accessible", steps: "Visit /docs. Verify Swagger UI loads." },
      { id: "health", label: "Health check", steps: "Visit /health or curl it. Verify status: healthy without collection data." },
      { id: "stats-endpoint", label: "GET /stats returns valid JSON", steps: "curl http://localhost:8000/stats. Verify response includes collection, propagation, financial, coverage, monthly_acquisitions, and activity keys." },
      { id: "settings-endpoint", label: "GET /settings includes nav_slots and theme", steps: "curl http://localhost:8000/settings. Verify nav_slots and theme keys are present with correct values." },
    ]
  },
];

function QAChecklist() {
  const [checks, setChecks] = useState({});
  const [notes, setNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [noteInput, setNoteInput] = useState("");
  const [expandedItem, setExpandedItem] = useState(null);
  const [runLabel, setRunLabel] = useState("");
  const [loaded, setLoaded] = useState(false);

  // Load saved state
  useEffect(() => {
    try {
      const c = localStorage.getItem("qa-checks");
      const n = localStorage.getItem("qa-notes");
      const l = localStorage.getItem("qa-run-label");
      if (c) setChecks(JSON.parse(c));
      if (n) setNotes(JSON.parse(n));
      if (l) setRunLabel(l);
    } catch (e) {}
    setLoaded(true);
  }, []);

  // Save state on change
  useEffect(() => {
    if (!loaded) return;
    localStorage.setItem("qa-checks", JSON.stringify(checks));
    localStorage.setItem("qa-notes", JSON.stringify(notes));
    localStorage.setItem("qa-run-label", runLabel);
  }, [checks, notes, runLabel, loaded]);

  const totalItems = CHECKLIST.reduce((sum, s) => sum + s.items.length, 0);
  const passCount = Object.values(checks).filter(v => v === "pass").length;
  const failCount = Object.values(checks).filter(v => v === "fail").length;
  const skipCount = Object.values(checks).filter(v => v === "skip").length;
  const doneCount = passCount + failCount + skipCount;

  const setCheck = (id, value) => {
    setChecks(prev => {
      const next = { ...prev };
      if (next[id] === value) { delete next[id]; } 
      else { next[id] = value; }
      return next;
    });
  };

  const saveNote = (id) => {
    if (noteInput.trim()) {
      setNotes(prev => ({ ...prev, [id]: noteInput.trim() }));
    } else {
      setNotes(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
    setEditingNote(null);
    setNoteInput("");
  };

  const resetAll = () => {
    if (window.confirm("Reset all checks and notes? This cannot be undone.")) {
      setChecks({});
      setNotes({});
      setRunLabel("");
    }
  };

  const sectionStats = (section) => {
    const p = section.items.filter(i => checks[i.id] === "pass").length;
    const f = section.items.filter(i => checks[i.id] === "fail").length;
    const s = section.items.filter(i => checks[i.id] === "skip").length;
    const total = section.items.length;
    return { p, f, s, done: p + f + s, total };
  };

  if (!loaded) return null;

  return (
    <div style={{
      fontFamily: "'IBM Plex Sans', 'SF Pro Text', system-ui, sans-serif",
      maxWidth: 720,
      margin: "0 auto",
      padding: "24px 16px",
      color: "#1a1a1a",
      background: "transparent",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <h1 style={{
            fontSize: 22,
            fontWeight: 600,
            fontFamily: "'IBM Plex Mono', monospace",
            margin: 0,
            letterSpacing: "-0.02em",
            color: "#3a5a40",
          }}>
            🌿 Aroidex QA
          </h1>
          <button onClick={resetAll} style={{
            background: "none", border: "1px solid #ccc", borderRadius: 6,
            padding: "4px 12px", fontSize: 12, color: "#888", cursor: "pointer",
          }}>Reset All</button>
        </div>

        {/* Run label */}
        <div style={{ marginTop: 12 }}>
          <input
            type="text"
            value={runLabel}
            onChange={e => setRunLabel(e.target.value)}
            placeholder="Label this run (e.g. 'v2.3 — propagation feature')"
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "8px 12px", border: "1px solid #ddd", borderRadius: 6,
              fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", color: "#333",
              background: "#fafafa",
            }}
          />
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#777", marginBottom: 4 }}>
            <span>{doneCount} / {totalItems} checked</span>
            <span style={{ display: "flex", gap: 10 }}>
              <span style={{ color: "#2d6a4f" }}>✓ {passCount}</span>
              <span style={{ color: "#c1121f" }}>✗ {failCount}</span>
              <span style={{ color: "#aaa" }}>— {skipCount}</span>
            </span>
          </div>
          <div style={{ height: 6, background: "#eee", borderRadius: 3, overflow: "hidden", display: "flex" }}>
            <div style={{ width: `${(passCount / totalItems) * 100}%`, background: "#2d6a4f", transition: "width 0.3s" }} />
            <div style={{ width: `${(failCount / totalItems) * 100}%`, background: "#c1121f", transition: "width 0.3s" }} />
            <div style={{ width: `${(skipCount / totalItems) * 100}%`, background: "#ccc", transition: "width 0.3s" }} />
          </div>
        </div>
      </div>

      {/* Sections */}
      {CHECKLIST.map(section => {
        const stats = sectionStats(section);
        return (
          <div key={section.section} style={{ marginBottom: 24 }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderBottom: "2px solid #3a5a40", paddingBottom: 6, marginBottom: 10,
            }}>
              <h2 style={{
                fontSize: 14, fontWeight: 600, textTransform: "uppercase",
                letterSpacing: "0.06em", margin: 0, color: "#3a5a40",
                fontFamily: "'IBM Plex Mono', monospace",
              }}>{section.section}</h2>
              <span style={{ fontSize: 11, color: "#888", fontFamily: "'IBM Plex Mono', monospace" }}>
                {stats.done}/{stats.total}
                {stats.f > 0 && <span style={{ color: "#c1121f", marginLeft: 6 }}>✗{stats.f}</span>}
              </span>
            </div>

            {section.items.map(item => {
              const status = checks[item.id];
              const note = notes[item.id];
              const isExpanded = expandedItem === item.id;

              return (
                <div key={item.id} style={{
                  padding: "8px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                    {/* Status buttons */}
                    <div style={{ display: "flex", gap: 2, flexShrink: 0, marginTop: 1 }}>
                      <button onClick={() => setCheck(item.id, "pass")} title="Pass" style={{
                        width: 26, height: 26, borderRadius: 4, border: "1px solid",
                        borderColor: status === "pass" ? "#2d6a4f" : "#ddd",
                        background: status === "pass" ? "#2d6a4f" : "white",
                        color: status === "pass" ? "white" : "#aaa",
                        cursor: "pointer", fontSize: 13, fontWeight: 600, lineHeight: 1,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>✓</button>
                      <button onClick={() => setCheck(item.id, "fail")} title="Fail" style={{
                        width: 26, height: 26, borderRadius: 4, border: "1px solid",
                        borderColor: status === "fail" ? "#c1121f" : "#ddd",
                        background: status === "fail" ? "#c1121f" : "white",
                        color: status === "fail" ? "white" : "#aaa",
                        cursor: "pointer", fontSize: 13, fontWeight: 600, lineHeight: 1,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>✗</button>
                      <button onClick={() => setCheck(item.id, "skip")} title="Skip" style={{
                        width: 26, height: 26, borderRadius: 4, border: "1px solid",
                        borderColor: status === "skip" ? "#888" : "#ddd",
                        background: status === "skip" ? "#888" : "white",
                        color: status === "skip" ? "white" : "#aaa",
                        cursor: "pointer", fontSize: 13, fontWeight: 600, lineHeight: 1,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>—</button>
                    </div>

                    {/* Label + actions */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                        <span
                          onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                          style={{
                            fontSize: 13.5, cursor: "pointer", lineHeight: 1.4,
                            textDecoration: status === "skip" ? "line-through" : "none",
                            color: status === "skip" ? "#aaa" : status === "fail" ? "#c1121f" : "#1a1a1a",
                          }}
                        >{item.label}</span>
                        <button
                          onClick={() => { setEditingNote(editingNote === item.id ? null : item.id); setNoteInput(note || ""); }}
                          style={{
                            background: "none", border: "none", cursor: "pointer",
                            fontSize: 11, color: note ? "#3a5a40" : "#bbb", padding: 0,
                          }}
                        >{note ? "📝" : "+"}</button>
                      </div>

                      {/* Expanded steps */}
                      {isExpanded && (
                        <div style={{
                          marginTop: 6, padding: "8px 10px",
                          background: "#f8f8f6", borderRadius: 6, fontSize: 12,
                          color: "#555", lineHeight: 1.5,
                          borderLeft: "3px solid #3a5a40",
                        }}>{item.steps}</div>
                      )}

                      {/* Note display */}
                      {note && editingNote !== item.id && (
                        <div style={{
                          marginTop: 4, fontSize: 11.5, color: "#777",
                          fontStyle: "italic", lineHeight: 1.4,
                        }}>↳ {note}</div>
                      )}

                      {/* Note editor */}
                      {editingNote === item.id && (
                        <div style={{ marginTop: 6, display: "flex", gap: 4 }}>
                          <input
                            type="text" autoFocus
                            value={noteInput}
                            onChange={e => setNoteInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && saveNote(item.id)}
                            placeholder="Add a note..."
                            style={{
                              flex: 1, padding: "4px 8px", fontSize: 12,
                              border: "1px solid #ccc", borderRadius: 4,
                              fontFamily: "'IBM Plex Sans', sans-serif",
                            }}
                          />
                          <button onClick={() => saveNote(item.id)} style={{
                            background: "#3a5a40", color: "white", border: "none",
                            borderRadius: 4, padding: "4px 10px", fontSize: 11, cursor: "pointer",
                          }}>Save</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Summary */}
      {doneCount === totalItems && (
        <div style={{
          marginTop: 20, padding: 16, borderRadius: 8,
          background: failCount === 0 ? "#f0faf0" : "#fef2f2",
          border: `1px solid ${failCount === 0 ? "#2d6a4f" : "#c1121f"}`,
          textAlign: "center", fontSize: 14,
          fontFamily: "'IBM Plex Mono', monospace",
        }}>
          {failCount === 0
            ? "✅ All checks passed — clear to deploy"
            : `⚠️ ${failCount} check${failCount > 1 ? "s" : ""} failed — review before deploy`}
        </div>
      )}
    </div>
  );
}

export default QAChecklist;
